import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Loader2 } from "lucide-react";
import { useTeamMembers } from "@/hooks/use-team-members";
import { useWorkspace } from "@/hooks/use-workspace";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function InviteMemberDialog({ open, onOpenChange }: Props) {
  const { currentWorkspace } = useWorkspace();
  const { inviteMember } = useTeamMembers();
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<"admin" | "member">("member");

  const handleClose = () => {
    setEmail("");
    setRole("member");
    onOpenChange(false);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim() || !currentWorkspace) return;
    inviteMember.mutate(
      { email, role },
      { onSuccess: handleClose },
    );
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Invite Team Member</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-5">
          <div className="space-y-1.5">
            <Label htmlFor="invite-email">Email address</Label>
            <Input
              id="invite-email"
              type="email"
              placeholder="colleague@example.com"
              value={email}
              onChange={e => setEmail(e.target.value)}
              required
            />
            {currentWorkspace && (
              <p className="text-xs text-muted-foreground">
                Inviting to <span className="font-medium text-foreground">{currentWorkspace.name}</span>. Switch workspaces to invite to a different one.
              </p>
            )}
          </div>

          <div className="space-y-2">
            <Label>Role</Label>
            <RadioGroup
              value={role}
              onValueChange={v => setRole(v as "admin" | "member")}
              className="space-y-2"
            >
              <label
                htmlFor="role-member"
                className="flex items-start gap-3 rounded-lg border border-border p-3 cursor-pointer has-[[data-state=checked]]:border-primary has-[[data-state=checked]]:bg-primary/5"
              >
                <RadioGroupItem value="member" id="role-member" className="mt-0.5" />
                <div>
                  <p className="text-sm font-medium">Team Member</p>
                  <p className="text-xs text-muted-foreground">
                    View-only by default. Customize write permissions after inviting.
                  </p>
                </div>
              </label>
              <label
                htmlFor="role-admin"
                className="flex items-start gap-3 rounded-lg border border-border p-3 cursor-pointer has-[[data-state=checked]]:border-primary has-[[data-state=checked]]:bg-primary/5"
              >
                <RadioGroupItem value="admin" id="role-admin" className="mt-0.5" />
                <div>
                  <p className="text-sm font-medium">Admin</p>
                  <p className="text-xs text-muted-foreground">
                    Full access to all features and settings.
                  </p>
                </div>
              </label>
            </RadioGroup>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={handleClose}>
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={inviteMember.isPending || !email.trim() || !currentWorkspace}
            >
              {inviteMember.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Send Invite
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
